<template>
  <div class="tcla-ai-export">
    <div class="tcla-panel">
      <h3 class="tcla-panel__title">🧠 تحلیل شکاف محتوایی، کنیبالیزیشن و لینک‌سازی با هوش مصنوعی</h3>
      <p class="tcla-panel__hint">
        تشخیص شکاف محتوایی و کنیبالیزیشن یک کار کاملاً معنایی است — الگوریتم‌های
        آماری در این کار قابل‌اعتماد نیستند. به‌جای حدس زدن، ساختار تمیز هر
        مقاله (تیترها، دسته‌بندی، امتیاز سئوی فعلی، و لینک‌های داخلی موجود) را
        استخراج کرده‌ایم؛ این را همراه با پرامپت آماده به هر هوش مصنوعی بدهید
        تا هم شکاف محتوایی و کنیبالیزیشن (شامل اینکه کدام مقاله بماند، کدام
        حذف/ادغام شود، و ریدایرکت به کجا باشد) را تحلیل کند، هم پیشنهاد
        لینک‌سازی داخلی بدهد.
      </p>

      <div class="tcla-options">
        <label class="tcla-checkbox">
          <input type="checkbox" v-model="includeH3">
          شامل تیترهای H3 باشد (حجم پرامپت را بیشتر می‌کند)
        </label>
        <div class="tcla-size-hint">
          حجم تقریبی JSON: <strong>{{ approxSizeLabel }}</strong>
        </div>
      </div>

      <div class="tcla-preview">
        <div class="tcla-preview__stat">
          <strong>{{ contentStructure.length }}</strong> مقاله آماده‌ی تحلیل ·
          <strong>{{ totalLinks }}</strong> لینک داخلی موجود در نقشه
        </div>
      </div>

      <div class="tcla-actions">
        <button class="tcla-btn tcla-btn--primary" @click="copyPromptWithData">
          📋 کپی پرامپت + داده (آماده برای چسباندن به AI)
        </button>
        <button class="tcla-btn" @click="copyDataOnly">📋 فقط کپی JSON ساختار</button>
        <button class="tcla-btn" @click="downloadDataOnly">⬇️ دانلود JSON</button>
      </div>

      <details class="tcla-preview-toggle">
        <summary>👁️ پیش‌نمایش پرامپتی که کپی می‌شود</summary>
        <pre class="tcla-prompt-preview">{{ promptText }}</pre>
      </details>

      <details class="tcla-preview-toggle">
        <summary>👁️ پیش‌نمایش داده‌ها ({{ contentStructure.length }} مقاله)</summary>
        <div class="tcla-article-preview" v-for="a in contentStructure.slice(0, 5)" :key="a.url">
          <strong>{{ a.title }}</strong>
          <span class="tcla-preview__meta">
            امتیاز {{ a.seoScore !== null ? a.seoScore : '-' }} ·
            {{ a.h2.length }} H2{{ includeH3 ? ' · ' + a.h3.length + ' H3' : '' }} ·
            {{ a.incomingLinks.length }} ورودی / {{ a.outgoingLinks.length }} خروجی
          </span>
        </div>
        <div v-if="contentStructure.length > 5" class="tcla-preview__more">
          و {{ contentStructure.length - 5 }} مقاله‌ی دیگر...
        </div>
      </details>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TCLAGaps',
  props: {
    contentStructure: { type: Array, default: () => [] }
  },
  data() {
    return {
      includeH3: false // off by default — keeps the prompt payload smaller
    };
  },
  computed: {
    totalLinks() {
      return this.contentStructure.reduce((s, a) => s + a.outgoingLinks.length, 0);
    },
    exportData() {
      if (this.includeH3) return this.contentStructure;
      // strip h3 to shrink payload when the user doesn't need it
      return this.contentStructure.map(a => {
        const { h3, ...rest } = a;
        return rest;
      });
    },
    approxSizeLabel() {
      const bytes = JSON.stringify(this.exportData).length;
      if (bytes < 1024) return `${bytes} بایت`;
      return `${Math.round(bytes / 1024)} کیلوبایت`;
    },
    promptText() {
      return `شما یک متخصص سئو و استراتژی محتوا هستید. در ادامه، فهرست تمام مقالات یک سایت به همراه عنوان، تیترها، دسته‌بندی/برچسب، امتیاز سئوی فعلی، و لینک‌های داخلی ورودی/خروجی هر مقاله (در قالب JSON) آمده است.

لطفاً این سه تحلیل را انجام بده:

۱. **کنیبالیزیشن کلمات کلیدی (Keyword Cannibalization)**
مقالاتی که موضوع و کلمه‌کلیدی هدف‌شان آنقدر به هم نزدیک است که ممکن است در نتایج گوگل با هم رقابت کنند را پیدا کن. برای هر گروه تداخل‌دار، دقیقاً مشخص کن:
- کدام مقالات با هم تداخل دارند و چرا
- **کدام مقاله باید بماند** (معمولاً امتیاز سئو/لینک ورودی بیشتر، محور اصلی)
- **کدام مقاله(ها) باید حذف یا در مقاله‌ی اصلی ادغام شوند** — و بعد از حذف، آدرس حذف‌شده به کدام آدرس ۳۰۱ ریدایرکت شود
- اگر ادغام پیشنهاد می‌شود: محتوای مقاله‌ی نگه‌داشته‌شده باید چه بخش‌هایی از مقاله‌ی حذف‌شده را هم پوشش دهد
- اگر تداخل واقعی نیست و باید هرکدام مستقل بمانند: چه تمایز موضوعی مشخصی باید بین‌شان ایجاد شود (تیتر، زاویه‌ی محتوا) تا دیگر با هم رقابت نکنند

۲. **شکاف محتوایی (Content Gap)**
بر اساس موضوعات و دسته‌بندی‌های موجود در داده، چه مقالات جدیدی برای تکمیل خوشه‌های موضوعی این سایت لازم است؟ برای هرکدام بگو:
- عنوان پیشنهادی
- کلمه کلیدی هدف پیشنهادی
- به کدام مقاله/دسته‌ی موجود مرتبط است

۳. **لینک‌سازی داخلی (Internal Linking)**
با توجه به لینک‌های ورودی/خروجی فعلی هر مقاله (incomingLinks / outgoingLinks در داده)، مقالاتی که از نظر موضوعی مرتبط هستند ولی هنوز به هم لینک نداده‌اند را پیدا کن و پیشنهاد بده:
- از کدام مقاله به کدام مقاله لینک داده شود
- چه انکرتکستی مناسب است

داده‌های سایت (JSON):
`;
    }
  },
  methods: {
    buildFullClipboardText() {
      return this.promptText + '\\n' + JSON.stringify(this.exportData, null, 2);
    },
    copyToClipboard(text, msg) {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.$emit('copy', text, msg);
    },
    copyPromptWithData() {
      this.copyToClipboard(this.buildFullClipboardText(), 'پرامپت + داده کپی شد — حالا در چت هوش مصنوعی بچسبانید');
    },
    copyDataOnly() {
      this.copyToClipboard(JSON.stringify(this.exportData, null, 2), 'JSON ساختار محتوا کپی شد');
    },
    downloadDataOnly() {
      const jsonStr = JSON.stringify(this.exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-structure-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.tcla-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 20px;
}

.tcla-panel__title { font-size: 0.95rem; font-weight: 700; margin: 0 0 8px 0; }
.tcla-panel__hint {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.7;
  margin: 0 0 16px 0;
  padding-bottom: 14px;
  border-bottom: 1px solid #f3f4f6;
}

.tcla-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  background: #f9fafb;
  border-radius: 0.5rem;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.tcla-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: #374151;
  cursor: pointer;
}
.tcla-checkbox input { cursor: pointer; }

.tcla-size-hint { font-size: 0.78rem; color: #9ca3af; }

.tcla-preview {
  background: #eff6ff;
  border-radius: 0.5rem;
  padding: 10px 14px;
  margin-bottom: 14px;
  font-size: 0.82rem;
  color: #1e40af;
}

.tcla-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.tcla-preview-toggle {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 10px 14px;
  margin-top: 10px;
}
.tcla-preview-toggle summary {
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  color: #4b5563;
}

.tcla-prompt-preview {
  white-space: pre-wrap;
  font-size: 0.72rem;
  color: #6b7280;
  background: #f9fafb;
  padding: 12px;
  border-radius: 0.375rem;
  margin-top: 10px;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
}

.tcla-article-preview {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  padding: 6px 0;
  border-bottom: 1px dashed #e5e7eb;
  gap: 8px;
}
.tcla-preview__meta { color: #9ca3af; font-size: 0.72rem; flex-shrink: 0; }
.tcla-preview__more { font-size: 0.75rem; color: #9ca3af; padding-top: 6px; }

.tcla-btn {
  padding: 8px 16px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.82rem;
  cursor: pointer;
}
.tcla-btn:hover { background: #e5e7eb; }
.tcla-btn--primary { background: #3b82f6; color: #fff; border-color: #3b82f6; }
.tcla-btn--primary:hover { background: #2563eb; }
</style>
